import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import {
  Users, CalendarClock, Receipt, TrendingUp, AlertTriangle, FileHeart, Wallet,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";

const rand = (n) => "R" + Number(n || 0).toLocaleString("en-ZA");

function Stat({ icon: Icon, label, value, sub, testid, tone = "primary" }) {
  return (
    <Card data-testid={testid} className="lift rounded-2xl border-border/60 p-5">
      <div className="flex items-center justify-between">
        <span className="label-eyebrow">{label}</span>
        <div className={`flex h-8 w-8 items-center justify-center rounded-lg bg-${tone}/10 text-${tone}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="mt-3 text-2xl font-bold tracking-tight">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard/stats")).data,
  });

  if (isLoading || !data)
    return <div className="text-muted-foreground">Loading dashboard…</div>;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
          Good day, {user?.name?.split(" ").slice(-1)}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Here's how Elysiom Dental is tracking today.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat testid="stat-patients" icon={Users} label="Active patients" value={data.patients} sub="On the books" />
        <Stat testid="stat-today" icon={CalendarClock} label="Upcoming" value={data.upcoming_today} sub={`${data.total_bookings} total bookings`} />
        <Stat testid="stat-revenue" icon={Wallet} label="Revenue (paid)" value={rand(data.revenue)} sub={`${rand(data.outstanding)} outstanding`} />
        <Stat testid="stat-noshow" icon={AlertTriangle} label="No-show rate" value={`${data.no_show_rate}%`} sub={`${data.completed} completed`} />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="rounded-2xl border-border/60 p-6 lg:col-span-2">
          <div className="mb-4 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            <h2 className="text-base font-semibold">Top procedures by volume</h2>
          </div>
          {data.top_procedures?.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.top_procedures} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "hsl(var(--secondary))" }} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--popover))" }} />
                <Bar dataKey="count" radius={[0, 8, 8, 0]}>
                  {data.top_procedures.map((_, i) => (
                    <Cell key={i} fill="hsl(var(--primary))" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground">No invoiced procedures yet.</p>
          )}
        </Card>

        <div className="space-y-4">
          <Stat testid="stat-claims" icon={FileHeart} label="Claims submitted" value={data.claims_submitted} sub={`${data.claim_rejection_rate}% rejection rate`} />
          <Stat testid="stat-outstanding" icon={Receipt} label="Outstanding" value={rand(data.outstanding)} sub="Patient liable balance" />
        </div>
      </div>
    </div>
  );
}
